import {
  IndicatorFactType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelFilter,
  REPORTING_FACT_BENCHMARKS,
} from '@/api/rest/resources/types/fact';
import {
  GeomodelServerGroupBy,
  GeomodelServerLabelFiltersOperation,
  GET_ORGANIZATION_GRAPH_FACTS_PATH,
  GET_ORGANIZATION_REPORTING_FACTS_PATH,
  OrganizationReportingFacts,
} from '@/api/rest/resources/types/organization';
import { useOrganizationHiddenFacts } from '@/hooks/useOrganizationHiddenFacts';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import { OrganizationFactQueryParams, useOrganizationFacts } from './useOrganizationFacts';
import { formatFactLabelsForRequest, getFact, getFactsFromFactArray } from './utils';

type UseOrganizationFactReturn<T, AsArray extends boolean> = AsArray extends true
  ? PlotReportFactElement<T | null>[]
  : Record<IndicatorFactType, PlotReportFactElement<T | null> | undefined>;

const useOrganizationFact = <T = unknown, AsArray extends boolean = false>(
  reportingFacts: OrganizationReportingFacts,
  type: IndicatorFactType | IndicatorFactType[],
  labelFilters?: PlotReportFactLabelFilter,
  asArray?: AsArray,
): UseOrganizationFactReturn<T, AsArray> => {
  const hiddenFacts = useOrganizationHiddenFacts();

  const factTypes = Array.isArray(type) ? type : [type];

  if (asArray) {
    return factTypes.flatMap((factType) => {
      const isFactHidden = hiddenFacts.includes(factType);
      const facts = getFactsFromFactArray<T>(isFactHidden ? undefined : reportingFacts.facts, factType, labelFilters);
      return facts ?? [];
    }) as UseOrganizationFactReturn<T, AsArray>;
  }

  return factTypes.reduce<Record<IndicatorFactType, PlotReportFactElement<T | null> | undefined>>(
    (acc, factType) => {
      const isFactHidden = hiddenFacts.includes(factType);

      const fact = getFact<T>(reportingFacts, factType, labelFilters, isFactHidden);
      if (fact) {
        return {
          ...acc,
          [factType]: fact,
        };
      }
      return acc;
    },
    {} as Record<IndicatorFactType, PlotReportFactElement<T | null> | undefined>,
  ) as UseOrganizationFactReturn<T, AsArray>;
};

type GeomodelServerOptions = {
  group_by?: GeomodelServerGroupBy[] | null;
  label_filters_operation?: GeomodelServerLabelFiltersOperation | null;
  source_ref?: string | null;
  limit?: number | null;
};

export const useOrganizationIndicatorReportingFact = <T = unknown, AsArray extends boolean = false>(
  type: IndicatorFactType | IndicatorFactType[],
  organizationIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  plotGroupId?: string | null,
  asArray?: AsArray,
  geomodelServerOptions?: GeomodelServerOptions,
) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;
  assertIsDefined(organizationId, 'organizationId');

  const filteredGeomodelServerOptions = Object.fromEntries(
    Object.entries(geomodelServerOptions ?? {}).filter(([, value]) => !isNullish(value)),
  ) as GeomodelServerOptions;

  const queryParams = {
    ...formatFactLabelsForRequest(labelFilters),
    fact_names: Array.isArray(type)
      ? type.filter((fact) => !isNullish(fact) && fact !== IndicatorName.unknown).join(',')
      : type,
    ...(filteredGeomodelServerOptions ?? {}),
  } as OrganizationFactQueryParams;

  const organizationReportingFacts = useOrganizationFacts(
    organizationId,
    GET_ORGANIZATION_REPORTING_FACTS_PATH,
    queryParams,
    plotGroupId,
  ).data;

  return useOrganizationFact<T, AsArray>(organizationReportingFacts, type, labelFilters, asArray);
};

/**
 * @param organizationId Falls back to the organizationId from the user if no value is provided.
 */
export const useOrganizationIndicatorGraphFact = <T = unknown>(
  type: IndicatorFactType | IndicatorFactType[],
  organizationIdArg?: string | null,
  labelFilters?: PlotReportFactLabelFilter,
  plotGroupId?: string | null,
  geomodelServerOptions?: GeomodelServerOptions,
) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;
  assertIsDefined(organizationId, 'organizationId');

  const filteredGeomodelServerOptions = Object.fromEntries(
    Object.entries(geomodelServerOptions ?? {}).filter(([, value]) => !isNullish(value)),
  ) as GeomodelServerOptions;

  const queryParams = {
    ...formatFactLabelsForRequest(labelFilters),
    fact_names: Array.isArray(type)
      ? type.filter((fact) => !isNullish(fact) && fact !== IndicatorName.unknown).join(',')
      : type,
    ...(filteredGeomodelServerOptions ?? {}),
  } as OrganizationFactQueryParams;

  let queryUrl: typeof GET_ORGANIZATION_GRAPH_FACTS_PATH | typeof GET_ORGANIZATION_REPORTING_FACTS_PATH =
    GET_ORGANIZATION_GRAPH_FACTS_PATH;

  if (!Array.isArray(type) && REPORTING_FACT_BENCHMARKS.includes(type)) {
    queryUrl = GET_ORGANIZATION_REPORTING_FACTS_PATH;
  }

  const organizationReportingFacts = useOrganizationFacts(organizationId, queryUrl, queryParams, plotGroupId).data;

  return useOrganizationFact<T>(organizationReportingFacts, type, labelFilters);
};
